import React, { memo } from 'react';
import { QrCodeIcon, TrophyIcon, Share2Icon, SmartphoneIcon, StarIcon, UsersIcon, TrendingUpIcon, CameraIcon, CalendarIcon, HeartIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SectionHeading } from '../components/ui/SectionHeading';
import { ScrollReveal } from '../components/ui/ScrollReveal';
import { ConceptCard } from '../components/ui/ConceptCard';
import { PuttingHero } from '../components/PuttingHero';
import { DesignConnectionGrid } from '../components/DesignConnectionGrid';
import { ShowcaseGallery } from '../components/ShowcaseGallery';
export function HomePage() {
  return <div className="w-full" data-id="element-604">
      {/* 1. HERO SECTION (Interactive) */}
      <PuttingHero data-id="element-605" />

      {/* 2. HOW WE DESIGN CONNECTION */}
      <DesignConnectionGrid data-id="element-606" />

      {/* 3. CONCEPTS + SHOWCASE */}
      <section className="py-32 bg-brand-green-deep text-brand-cream-light" data-id="element-607">
        <div className="max-w-7xl mx-auto px-6" data-id="element-608">
          <SectionHeading eyebrow="Signature Concepts" title="Resort-ready experiences." subtitle="From poolside challenge greens to sunset putting lounges and wedding-weekend activations, PuttLab creates resort concepts that drive guest engagement, social sharing, and repeat visits." light className="mb-20" data-id="element-609" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-id="element-610">
            <ConceptCard title="The Resort Green" gradientClass="bg-concept-resort" imageUrl="https://cdn.magicpatterns.com/uploads/s7i8aMmZ7R3Wg5fbHMCJgk/WhatsApp_Image_2026-03-17_at_12.28.34.jpg" delay={0.1} data-id="element-611" />
            <ConceptCard title="The Sunset Green" gradientClass="bg-concept-sunset" imageUrl="https://cdn.magicpatterns.com/uploads/tvAkCsifm3oKpj9V73sDzg/sunet_green.png" delay={0.2} data-id="element-612" />
            <ConceptCard title="The Challenge Green" gradientClass="bg-concept-challenge" imageUrl="https://cdn.magicpatterns.com/uploads/97PxqU37s7pQE7TC6nRynb/challenge_green.png" delay={0.3} data-id="element-613" />
            <ConceptCard title="The Social Green" gradientClass="bg-concept-social" imageUrl="https://cdn.magicpatterns.com/uploads/27Tjnbykmz8BtCjWmDRoGL/socialgreen.png" delay={0.4} data-id="element-614" />
            <ConceptCard title="The Wedding Green" gradientClass="bg-concept-wedding" imageUrl="https://cdn.magicpatterns.com/uploads/o2kbb1M8SM3uegyLFntqUo/wedding_golf.png" delay={0.5} data-id="element-615" />
            <ConceptCard title="The Estate Green" gradientClass="bg-concept-estate" imageUrl="https://cdn.magicpatterns.com/uploads/3b62EKAAjmaPYQxrkuwQqy/estategreen.png" delay={0.6} data-id="element-616" />
          </div>
        </div>

        {/* Visual Showcase */}
        <ShowcaseGallery data-id="element-617" />
      </section>

      {/* LICENSED EXPERIENCES */}

      {/* 4. WHY RESORTS LOVE US */}
      <section className="py-32 bg-brand-charcoal text-brand-cream-light relative overflow-hidden" data-id="element-618">
        <div className="contour-pattern absolute inset-0 opacity-10 pointer-events-none" data-id="element-619" />
        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-620">
          <SectionHeading eyebrow="Resort Impact" title="Why resorts love PuttLab." subtitle="We don't just add an amenity  we create the experience guests talk about at dinner, post on Instagram, and come back for next year." align="center" light className="mb-24" data-id="element-621" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-622">
            {[{
            icon: TrendingUpIcon,
            title: 'Drives guest engagement',
            desc: 'A PuttLab green becomes the most active social space on property — outperforming traditional amenities in dwell time and repeat use.'
          }, {
            icon: CameraIcon,
            title: 'Built for social sharing',
            desc: 'Every angle is designed for the camera. Guests create organic content that markets your resort for free  sunset putts, winner celebrations, family moments.'
          }, {
            icon: UsersIcon,
            title: 'All ages, all skill levels',
            desc: 'From kids to couples to corporate groups  PuttLab greens are designed so everyone can play, compete, and connect.'
          }, {
            icon: StarIcon,
            title: 'Elevates your brand',
            desc: "A PuttLab installation signals that your resort thinks differently about guest experience. It', s, a, premium, differentiator, a, competitive, market, .,"
          }, {
            icon: CalendarIcon,
            title: 'Activates underused space',
            desc: 'Pool decks, garden paths, rooftops, beachfront lawns  we transform dead zones into the most magnetic spot on property.'
          }, {
            icon: HeartIcon,
            title: 'Creates lasting memories',
            desc: 'Guests remember the putt they sank at sunset, the challenge they won by the pool. PuttLab turns a stay into a story.'
          }].map((item, i) => <ScrollReveal key={item.title} delay={i * 0.1} data-id="element-623">
                <div className="glass-panel p-8 rounded-2xl h-full" data-id="element-624">
                  <div className="w-12 h-12 rounded-full bg-brand-gold/10 flex items-center justify-center mb-6" data-id="element-625">
                    <item.icon className="w-6 h-6 text-brand-gold" data-id="element-626" />
                  </div>
                  <h3 className="text-xl font-heading text-brand-cream-light mb-3" data-id="element-627">
                    {item.title}
                  </h3>
                  <p className="text-brand-cream/70 font-body leading-relaxed" data-id="element-628">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* 5. THE GAP — Why resorts underinvest */}
      <section className="py-32 bg-brand-green-deep text-brand-cream-light relative overflow-hidden" data-id="element-629">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(184,150,90,0.1)_0%,transparent_50%)]" data-id="element-630" />
        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-631">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start" data-id="element-632">
            {/* Left — The Problem */}
            <div data-id="element-633">
              <ScrollReveal data-id="element-634">
                <span className="block text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-4" data-id="element-635">
                  The Industry Gap
                </span>
                <h2 className="text-4xl md:text-5xl font-heading leading-tight mb-8" data-id="element-636">
                  Most resorts still treat putting as a cheap side amenity.
                </h2>
                <p className="text-lg font-body text-brand-cream/70 mb-12 leading-relaxed" data-id="element-637">
                  The hospitality industry underinvests in golf experiences for
                  five predictable reasons:
                </p>
              </ScrollReveal>

              <div className="space-y-6" data-id="element-638">
                {[{
                num: '01',
                text: 'They assume it looks childish or low-end'
              }, {
                num: '02',
                text: 'They prioritize pools, F&B, spas, and rooms first'
              }, {
                num: '03',
                text: "They don't have a team that knows how to design it beautifully"
              }, {
                num: '04',
                text: "They assume it won't drive enough ROI"
              }, {
                num: '05',
                text: 'Badly designed mini golf has trained the market to expect mediocre'
              }].map((item, i) => <ScrollReveal key={item.num} delay={i * 0.1} data-id="element-639">
                    <div className="flex items-start space-x-4 group" data-id="element-640">
                      <span className="text-brand-gold/40 font-heading text-lg mt-0.5 shrink-0" data-id="element-641">
                        {item.num}
                      </span>
                      <p className="text-brand-cream/80 font-body text-lg leading-relaxed border-l border-brand-cream/10 pl-4" data-id="element-642">
                        {item.text}
                      </p>
                    </div>
                  </ScrollReveal>)}
              </div>
            </div>

            {/* Right — The Opportunity / PuttLab's Answer */}
            <div className="lg:sticky lg:top-32" data-id="element-643">
              <ScrollReveal delay={0.3} data-id="element-644">
                <div className="glass-panel rounded-2xl p-10 md:p-12" data-id="element-645">
                  <span className="block text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-6" data-id="element-646">
                    The Opportunity
                  </span>
                  <h3 className="text-3xl md:text-4xl font-heading text-brand-cream-light leading-tight mb-6" data-id="element-647">
                    Almost nobody has reimagined putting as{' '}
                    <i className="text-brand-gold" data-id="element-648">
                      social, aesthetic, shareable, and hospitality-grade.
                    </i>
                  </h3>
                  <p className="text-brand-cream/70 font-body text-lg leading-relaxed mb-8" data-id="element-649">
                    That's the gap. And that's exactly what PuttLab was built to
                    fill. We don't do mini golf. We design premium putting
                    experiences that look like they belong at the best resorts
                    in the world — because they do.
                  </p>
                  <div className="border-t border-brand-cream/10 pt-8 space-y-4" data-id="element-650">
                    <div className="flex items-center space-x-3" data-id="element-651">
                      <div className="w-2 h-2 rounded-full bg-brand-gold" data-id="element-652" />
                      <span className="text-brand-cream/80 font-body" data-id="element-653">
                        Architecturally beautiful, not theme-park tacky
                      </span>
                    </div>
                    <div className="flex items-center space-x-3" data-id="element-654">
                      <div className="w-2 h-2 rounded-full bg-brand-gold" data-id="element-655" />
                      <span className="text-brand-cream/80 font-body" data-id="element-656">
                        Designed for Instagram, not just foot traffic
                      </span>
                    </div>
                    <div className="flex items-center space-x-3" data-id="element-657">
                      <div className="w-2 h-2 rounded-full bg-brand-gold" data-id="element-658" />
                      <span className="text-brand-cream/80 font-body" data-id="element-659">
                        Built to drive engagement, revenue, and repeat visits
                      </span>
                    </div>
                    <div className="flex items-center space-x-3" data-id="element-660">
                      <div className="w-2 h-2 rounded-full bg-brand-gold" data-id="element-661" />
                      <span className="text-brand-cream/80 font-body" data-id="element-662">
                        Hospitality-grade durability meets luxury aesthetics
                      </span>
                    </div>
                  </div>
                  <div className="mt-10" data-id="element-663">
                    <Button variant="primary" href="/contact" data-id="element-664">
                      Close the Gap at Your Resort
                    </Button>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* 6. AMENITY IMPACT / TRANSFORMATIONS */}
      <section className="py-32 bg-brand-cream" data-id="element-665">
        <div className="max-w-7xl mx-auto px-6" data-id="element-666">
          <SectionHeading eyebrow="Transformations" title="From overlooked space to unforgettable destination." subtitle="Every resort has untapped potential. We see it, design it, and build it." className="mb-16" data-id="element-667" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-668">
            {[{
            before: 'A pool deck corner',
            after: 'A guest challenge green'
          }, {
            before: 'A beachfront lawn',
            after: 'A sunset putting lounge'
          }, {
            before: 'A garden pathway',
            after: 'A 9-hole resort trail'
          }, {
            before: 'A conference patio',
            after: 'A corporate team-building arena'
          }].map((item, i) => <ScrollReveal key={i} delay={i * 0.1} data-id="element-669">
                <div className="bg-brand-cream-light p-8 md:p-12 rounded-sm border border-brand-stone/20 flex flex-col justify-center min-h-[200px] hover:border-brand-gold/50 transition-colors" data-id="element-670">
                  <p className="text-brand-stone font-body text-lg mb-2 line-through decoration-brand-stone/50" data-id="element-671">
                    {item.before}
                  </p>
                  <p className="text-3xl font-heading text-brand-green-deep" data-id="element-672">
                    becomes <i className="text-brand-gold" data-id="element-673">{item.after}</i>.
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* 6. PUTTLAB PLAY */}
      <section className="py-32 bg-brand-charcoal text-brand-cream-light relative overflow-hidden" data-id="element-674">
        <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#C4A97D_1px,transparent_1px)] [background-size:32px_32px]" data-id="element-675" />

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-676">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-677">
            <div data-id="element-678">
              <ScrollReveal data-id="element-679">
                <span className="block text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-4" data-id="element-680">
                  Digital Layer
                </span>
                <h2 className="text-4xl md:text-5xl font-heading leading-tight mb-6" data-id="element-681">
                  A guest experience that lives beyond the green.
                </h2>
                <p className="text-lg font-body text-brand-cream/80 mb-10 leading-relaxed" data-id="element-682">
                  With PuttLab Play, resort guests scan a QR code to unlock game
                  modes, keep score, join challenges, view leaderboards, and
                  generate shareable winner cards. It's a digital layer that
                  turns your putting green into an experience guests replay,
                  post, and talk about at dinner.
                </p>
              </ScrollReveal>

              <div className="space-y-6" data-id="element-683">
                {[{
                icon: QrCodeIcon,
                text: 'Guests scan to start'
              }, {
                icon: SmartphoneIcon,
                text: 'Choose a challenge mode'
              }, {
                icon: TrophyIcon,
                text: 'Compete and keep score'
              }, {
                icon: Share2Icon,
                text: 'Share the win on social'
              }].map((feature, i) => <ScrollReveal key={feature.text} delay={0.2 + i * 0.1} data-id="element-684">
                    <div className="flex items-center space-x-4" data-id="element-685">
                      <div className="w-10 h-10 rounded-full bg-brand-green flex items-center justify-center" data-id="element-686">
                        <feature.icon className="w-5 h-5 text-brand-gold" data-id="element-687" />
                      </div>
                      <span className="font-body text-lg text-brand-cream-light" data-id="element-688">
                        {feature.text}
                      </span>
                    </div>
                  </ScrollReveal>)}
              </div>

              <ScrollReveal delay={0.6} data-id="element-689">
                <div className="mt-12" data-id="element-690">
                  <Button variant="primary" href="/play" data-id="element-691">
                    Explore PuttLab Play
                  </Button>
                </div>
              </ScrollReveal>
            </div>

            {/* Mockup visual — Winner/Share screen */}
            <ScrollReveal direction="left" delay={0.3} data-id="element-692">
              <div className="relative mx-auto w-full max-w-sm aspect-[9/19] bg-brand-green-deep rounded-[3rem] border-8 border-brand-dark-gray shadow-2xl overflow-hidden flex flex-col" data-id="element-693">
                <div className="absolute top-0 inset-x-0 h-6 bg-brand-dark-gray rounded-b-3xl w-1/2 mx-auto z-20" data-id="element-694" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(184,150,90,0.2)_0%,transparent_60%)]" data-id="element-695" />
                <div className="flex-1 bg-gradient-to-b from-brand-green to-brand-green-deep p-6 pt-14 flex flex-col relative z-10" data-id="element-696">
                  <div className="text-center mb-6" data-id="element-697">
                    <span className="text-brand-gold text-[9px] font-bold uppercase tracking-[0.25em] block mb-1" data-id="element-698">
                      🏆 Course Champion
                    </span>
                    <h4 className="font-heading text-3xl text-brand-cream-light mb-0.5" data-id="element-699">
                      Alex M.
                    </h4>
                    <p className="text-xs font-body text-brand-cream/50 uppercase tracking-widest" data-id="element-700">
                      The Sunset Green
                    </p>
                  </div>

                  <div className="bg-brand-charcoal/40 backdrop-blur-md rounded-2xl p-5 border border-brand-cream/10 mb-4 text-center" data-id="element-701">
                    <span className="text-brand-cream/50 text-[10px] uppercase tracking-widest block mb-1" data-id="element-702">
                      Final Score
                    </span>
                    <span className="text-6xl font-heading text-brand-gold leading-none" data-id="element-703">
                      18
                    </span>
                    <span className="text-brand-cream/40 text-xs block mt-1 uppercase tracking-wider" data-id="element-704">
                      Strokes · 9 Holes
                    </span>
                  </div>

                  <div className="bg-brand-cream-light/5 rounded-xl border border-brand-cream/10 mb-4 overflow-hidden flex-1" data-id="element-705">
                    <div className="px-4 py-2 border-b border-brand-cream/10" data-id="element-706">
                      <span className="text-[9px] text-brand-gold font-bold uppercase tracking-widest" data-id="element-707">
                        Leaderboard
                      </span>
                    </div>
                    <div className="px-4 py-2.5 flex items-center justify-between border-b border-brand-cream/5" data-id="element-708">
                      <div className="flex items-center space-x-2" data-id="element-709">
                        <span className="text-brand-gold text-xs font-bold w-4" data-id="element-710">
                          1
                        </span>
                        <span className="text-brand-cream-light text-sm font-medium" data-id="element-711">
                          Alex M.
                        </span>
                      </div>
                      <span className="text-brand-gold text-sm font-heading" data-id="element-712">
                        18
                      </span>
                    </div>
                    <div className="px-4 py-2.5 flex items-center justify-between border-b border-brand-cream/5" data-id="element-713">
                      <div className="flex items-center space-x-2" data-id="element-714">
                        <span className="text-brand-cream/40 text-xs font-bold w-4" data-id="element-715">
                          2
                        </span>
                        <span className="text-brand-cream/70 text-sm" data-id="element-716">
                          Sarah K.
                        </span>
                      </div>
                      <span className="text-brand-cream/70 text-sm font-heading" data-id="element-717">
                        21
                      </span>
                    </div>
                    <div className="px-4 py-2.5 flex items-center justify-between" data-id="element-718">
                      <div className="flex items-center space-x-2" data-id="element-719">
                        <span className="text-brand-cream/40 text-xs font-bold w-4" data-id="element-720">
                          3
                        </span>
                        <span className="text-brand-cream/70 text-sm" data-id="element-721">
                          Mike R.
                        </span>
                      </div>
                      <span className="text-brand-cream/70 text-sm font-heading" data-id="element-722">
                        24
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3" data-id="element-723">
                    <div className="bg-brand-gold text-brand-charcoal rounded-xl py-3 text-center font-bold text-xs uppercase tracking-wider" data-id="element-724">
                      Share Result
                    </div>
                    <div className="bg-brand-cream-light/10 text-brand-cream-light rounded-xl py-3 text-center font-bold text-xs uppercase tracking-wider border border-brand-cream/10" data-id="element-725">
                      Play Again
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 7. CREDIBILITY / TRUSTED BY */}
      <section className="py-24 bg-brand-green text-brand-cream-light" data-id="element-726">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-727">
          <ScrollReveal data-id="element-728">
            <h2 className="text-3xl md:text-4xl font-heading mb-8 leading-relaxed" data-id="element-729">
              Built on real-world experience. Designed for world-class
              hospitality.
            </h2>
            <p className="text-lg font-body text-brand-cream/80 mb-12" data-id="element-730">
              PuttLab is the premium evolution of TurfsUp! — with nearly a
              decade of build expertise and 1000+ completed projects across
              North America. Now we're bringing that proven capability to
              resorts and destinations in Cancún, the Caribbean, and beyond.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-8 sm:space-y-0 sm:space-x-16 border-t border-brand-cream/20 pt-12" data-id="element-731">
              <div data-id="element-732">
                <span className="block text-5xl font-heading text-brand-gold mb-2" data-id="element-733">
                  10<span className="text-3xl" data-id="element-734">yrs</span>
                </span>
                <span className="text-sm font-body uppercase tracking-widest text-brand-cream/60" data-id="element-735">
                  Experience
                </span>
              </div>
              <div data-id="element-736">
                <span className="block text-5xl font-heading text-brand-gold mb-2" data-id="element-737">
                  1000<span className="text-3xl" data-id="element-738">+</span>
                </span>
                <span className="text-sm font-body uppercase tracking-widest text-brand-cream/60" data-id="element-739">
                  Projects Completed
                </span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 8. WHY PUTTLAB */}
      <section className="py-32 bg-brand-cream-light" data-id="element-740">
        <div className="max-w-7xl mx-auto px-6" data-id="element-741">
          <SectionHeading eyebrow="The Difference" title="Why PuttLab" align="center" className="mb-20" data-id="element-742" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16" data-id="element-743">
            {[{
            title: 'Design-led',
            desc: 'Every project begins with guest experience, flow, and visual impact  not just turf specs.'
          }, {
            title: 'Social by nature',
            desc: 'Our environments are built for connection, competition, and the kind of moments guests share.'
          }, {
            title: 'Built for sharing',
            desc: 'Each space is designed to create photo moments, bragging rights, and organic resort content.'
          }, {
            title: 'Hospitality-native',
            desc: 'We understand resort operations, guest flow, durability needs, and high-traffic environments.'
          }, {
            title: 'Crafted to perform',
            desc: 'Behind the beauty is real contouring, premium materials, and build knowledge that lasts.'
          }, {
            title: 'Backed by TurfsUp!',
            desc: 'Built on nearly a decade of experience and 1000+ completed projects in North America.'
          }].map((pillar, i) => <ScrollReveal key={pillar.title} delay={i * 0.1} data-id="element-744">
                <div className="border-t border-brand-stone/30 pt-6" data-id="element-745">
                  <h3 className="text-2xl font-heading text-brand-green-deep mb-3" data-id="element-746">
                    {pillar.title}
                  </h3>
                  <p className="text-brand-stone font-body" data-id="element-747">{pillar.desc}</p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* 9. PROCESS */}
      <section className="py-32 bg-brand-cream" data-id="element-748">
        <div className="max-w-7xl mx-auto px-6" data-id="element-749">
          <SectionHeading eyebrow="How We Work" title="Process" align="center" className="mb-20" data-id="element-750" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative" data-id="element-751">
            <div className="hidden md:block absolute top-12 left-0 w-full h-[1px] bg-brand-stone/30" data-id="element-752" />

            {[{
            num: '1',
            title: 'Discover',
            desc: 'We learn the property, the guest profile, and the opportunity.'
          }, {
            num: '2',
            title: 'Design',
            desc: 'We shape a concept that blends play, beauty, and resort-level usability.'
          }, {
            num: '3',
            title: 'Build',
            desc: 'We execute with premium materials, contour precision, and craftsmanship.'
          }, {
            num: '4',
            title: 'Activate',
            desc: 'We launch the experience with PuttLab Play, signage, and guest programming.'
          }].map((step, i) => <ScrollReveal key={step.num} delay={i * 0.2} className="relative z-10" data-id="element-753">
                <div className="flex flex-col items-center text-center" data-id="element-754">
                  <div className="w-24 h-24 rounded-full bg-brand-cream-light border-4 border-brand-cream flex items-center justify-center mb-6 shadow-lg" data-id="element-755">
                    <span className="text-3xl font-heading text-brand-gold" data-id="element-756">
                      {step.num}
                    </span>
                  </div>
                  <h3 className="text-2xl font-heading text-brand-green-deep mb-3" data-id="element-757">
                    {step.title}
                  </h3>
                  <p className="text-brand-stone font-body" data-id="element-758">{step.desc}</p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section className="py-32 bg-brand-green-deep text-brand-cream-light relative overflow-hidden" data-id="element-759">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(184,150,90.2)_0%,transparent_50%)]" data-id="element-760" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-761">
          <ScrollReveal data-id="element-762">
            <h2 className="text-5xl md:text-7xl font-heading mb-8" data-id="element-763">
              Let's transform your resort.
            </h2>
            <p className="text-xl font-body text-brand-cream/80 mb-12 max-w-2xl mx-auto" data-id="element-764">
              Whether you're in Cancún, the Caribbean, or anywhere guests
              deserve an unforgettable experience — PuttLab is ready to design
              it.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-765">
              <Button variant="primary" size="lg" href="/contact" data-id="element-766">
                Start Your Resort Project
              </Button>
              <Button variant="secondary" size="lg" href="/contact" className="!text-brand-cream-light !border-brand-cream-light/70 hover:!bg-brand-cream-light hover:!text-brand-green-deep" data-id="element-767">
                Talk to PuttLab
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}